import { supabase, Campaign, QRCode } from '../lib/supabase';

export interface CampaignData {
  name: string;
  title?: string;
  description?: string;
  type: 'Promotional' | 'Brand Awareness' | 'Seasonal' | 'Product Launch';
  qr_type: 'Static QR' | 'Dynamic QR';
  cta: string;
  avatar_color: 'green' | 'yellow' | 'blue' | 'purple' | 'red';
  industry?: string[];
  goal?: string;
  snapgoal?: string[];
  media_type?: string[];
  start_date?: string;
  end_date?: string;
  preview_img?: string;
  ad_content?: any;
  budget?: number;
  selected_nodes?: number[];
}

export interface QRCodeData {
  name: string;
  qr_type: 'Static QR' | 'Dynamic QR';
  destination_url: string;
  cta: string;
  design_settings?: any;
  expires_at?: string;
}

class CampaignService {
  // Create a new campaign
  async createCampaign(userId: string, campaignData: CampaignData): Promise<{ campaign: Campaign; qrCode?: QRCode; error?: string }> {
    try {
      // Generate unique campaign ID
      const campaignId = `campaign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Prepare campaign data for insertion
      const campaignInsert = {
        id: campaignId,
        user_id: userId,
        name: campaignData.name,
        title: campaignData.title || campaignData.name,
        description: campaignData.description,
        type: campaignData.type,
        status: 'Active' as const,
        qr_type: campaignData.qr_type,
        cta: campaignData.cta,
        avatar_color: campaignData.avatar_color,
        industry: campaignData.industry || [],
        goal: campaignData.goal,
        snapgoal: campaignData.snapgoal || [],
        media_type: campaignData.media_type || [],
        start_date: campaignData.start_date,
        end_date: campaignData.end_date,
        preview_img: campaignData.preview_img,
        ad_content: campaignData.ad_content || {},
        budget: campaignData.budget || 0,
        spent_amount: 0,
        total_scans: 0,
        conversion_rate: 0,
        peak_hours: '12:00-13:00',
        top_location: 'N/A',
        performance_metrics: {
          impressions: 0,
          clicks: 0,
          conversions: 0,
          selected_nodes: campaignData.selected_nodes || []
        }
      };

      // Insert campaign
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert(campaignInsert)
        .select()
        .single();

      if (campaignError) {
        console.error('Error creating campaign:', campaignError);
        return { campaign: null as any, error: campaignError.message };
      }

      // Create QR code if needed
      let qrCode: QRCode | undefined;
      if (campaignData.qr_type) {
        const qrResult = await this.createQRCode(userId, campaignId, {
          name: `${campaignData.name} QR`,
          qr_type: campaignData.qr_type,
          destination_url: campaignData.ad_content?.qr_url || 'https://example.com',
          cta: campaignData.cta,
          design_settings: campaignData.ad_content?.qr_design || {},
          expires_at: campaignData.qr_type === 'Dynamic QR' ? campaignData.end_date : undefined
        });

        if (qrResult.error) {
          console.warn('QR code creation failed:', qrResult.error);
        } else {
          qrCode = qrResult.qrCode;
        }
      }

      // Update user's campaign count
      await this.updateUserCampaignCount(userId);

      return { campaign, qrCode };
    } catch (error) {
      console.error('Error in createCampaign:', error);
      return { campaign: null as any, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Create a QR code
  async createQRCode(userId: string, campaignId: string, qrData: QRCodeData): Promise<{ qrCode: QRCode; error?: string }> {
    try {
      const qrId = `qr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Generate QR code URL (you can use a QR generation service here)
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData.destination_url)}`;

      const qrInsert = {
        id: qrId,
        user_id: userId,
        campaign_id: campaignId,
        name: qrData.name,
        qr_type: qrData.qr_type,
        qr_code_url: qrCodeUrl,
        destination_url: qrData.destination_url,
        cta: qrData.cta,
        design_settings: qrData.design_settings || {},
        total_scans: 0,
        status: 'Active' as const,
        expires_at: qrData.expires_at
      };

      const { data: qrCode, error } = await supabase
        .from('qr_codes')
        .insert(qrInsert)
        .select()
        .single();

      if (error) {
        console.error('Error creating QR code:', error);
        return { qrCode: null as any, error: error.message };
      }

      // Update user's QR code count
      await this.updateUserQRCount(userId);

      return { qrCode };
    } catch (error) {
      console.error('Error in createQRCode:', error);
      return { qrCode: null as any, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Get user's campaigns
  async getUserCampaigns(userId: string): Promise<{ campaigns: Campaign[]; error?: string }> {
    try {
      const { data: campaigns, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching campaigns:', error);
        return { campaigns: [], error: error.message };
      }

      return { campaigns: campaigns || [] };
    } catch (error) {
      console.error('Error in getUserCampaigns:', error);
      return { campaigns: [], error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Get user's QR codes
  async getUserQRCodes(userId: string): Promise<{ qrCodes: QRCode[]; error?: string }> {
    try {
      const { data: qrCodes, error } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching QR codes:', error);
        return { qrCodes: [], error: error.message };
      }

      return { qrCodes: qrCodes || [] };
    } catch (error) {
      console.error('Error in getUserQRCodes:', error);
      return { qrCodes: [], error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Get all campaigns for admin with pagination
  async getAllCampaigns(
    page = 1,
    pageSize = 50,
    dateRange?: { startDate?: string; endDate?: string }
  ): Promise<{
    campaigns: Campaign[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
    error?: string;
  }> {
    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build the query with pagination
      let query = supabase
        .from('campaigns')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Apply date range filter if provided
      if (dateRange?.startDate) {
        query = query.gte('created_at', dateRange.startDate);
      }
      if (dateRange?.endDate) {
        query = query.lte('created_at', dateRange.endDate);
      }

      // Apply pagination range
      const { data: campaigns, error, count } = await query.range(from, to);

      if (error) {
        console.error('Error fetching all campaigns:', error);
        return {
          campaigns: [],
          totalCount: 0,
          page,
          pageSize,
          totalPages: 0,
          error: error.message
        };
      }

      let campaignsWithProfiles = campaigns || [];

      // Fetch user profiles separately if we have campaigns
      if (campaignsWithProfiles.length > 0) {
        const userIds = Array.from(new Set(campaignsWithProfiles.map(c => c.user_id).filter(Boolean)));

        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, name, email, business_name')
            .in('id', userIds);

          if (!profilesError && profilesData) {
            // Create a map of user_id to profile data
            const profilesMap = profilesData.reduce((acc, profile) => {
              acc[profile.id] = profile;
              return acc;
            }, {} as Record<string, any>);

            // Add profile data to campaigns
            campaignsWithProfiles = campaignsWithProfiles.map(campaign => ({
              ...campaign,
              profiles: campaign.user_id ? profilesMap[campaign.user_id] : null
            }));
          }
        }
      }

      return {
        campaigns: campaignsWithProfiles,
        totalCount: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    } catch (error) {
      console.error('Error in getAllCampaigns:', error);
      return {
        campaigns: [],
        totalCount: 0,
        page,
        pageSize,
        totalPages: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Get analytics data
  async getAnalyticsData(userId: string): Promise<{ analytics: any; error?: string }> {
    try {
      // Get campaigns
      const { campaigns } = await this.getUserCampaigns(userId);

      // Get QR codes with their scan counts from qr_scans table
      const { qrCodes } = await this.getUserQRCodes(userId);
      const qrCodeIds = qrCodes.map(qr => qr.id);

      // Get QR scans from qr_scans table (single source of truth)
      const { data: qrScans, error: scansError } = await supabase
        .from('qr_scans')
        .select('id, qr_code_id, scan_timestamp')
        .in('qr_code_id', qrCodeIds);

      if (scansError) {
        console.warn('Error fetching QR scans:', scansError);
      }

      // Calculate total scans dynamically by counting qr_scans rows
      const totalScans = qrScans?.length || 0;

      // Calculate analytics
      const totalCampaigns = campaigns.length;
      const activeCampaigns = campaigns.filter(c => c.status === 'Active').length;
      const totalQRCodes = qrCodes.length;
      const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
      const totalSpent = campaigns.reduce((sum, c) => sum + (c.spent_amount || 0), 0);

      // Calculate scan trends (last 7 days) from qr_scans table
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      const scanTrends = last7Days.map(date => {
        const dayScans = (qrScans || []).filter(scan =>
          scan.scan_timestamp?.startsWith(date)
        ).length;
        return { date, scans: dayScans };
      });

      // Calculate scans per campaign by grouping qr_scans by campaign_id through qr_codes
      const campaignScansMap = new Map<string, number>();
      for (const qrCode of qrCodes) {
        if (qrCode.campaign_id) {
          const scansForQR = (qrScans || []).filter(scan => scan.qr_code_id === qrCode.id).length;
          const currentCount = campaignScansMap.get(qrCode.campaign_id) || 0;
          campaignScansMap.set(qrCode.campaign_id, currentCount + scansForQR);
        }
      }

      return {
        analytics: {
          totalCampaigns,
          activeCampaigns,
          totalQRCodes,
          totalScans,
          totalBudget,
          totalSpent,
          scanTrends,
          campaignPerformance: campaigns.map(c => ({
            id: c.id,
            name: c.name,
            scans: campaignScansMap.get(c.id) || 0,
            conversionRate: c.conversion_rate,
            status: c.status
          }))
        }
      };
    } catch (error) {
      console.error('Error in getAnalyticsData:', error);
      return { analytics: null, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Update user campaign count
  private async updateUserCampaignCount(userId: string): Promise<void> {
    try {
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id')
        .eq('user_id', userId);

      const count = campaigns?.length || 0;

      await supabase
        .from('profiles')
        .update({ total_campaigns: count })
        .eq('id', userId);
    } catch (error) {
      console.warn('Failed to update user campaign count:', error);
    }
  }

  // Update user QR count
  private async updateUserQRCount(userId: string): Promise<void> {
    try {
      const { data: qrCodes } = await supabase
        .from('qr_codes')
        .select('id')
        .eq('user_id', userId);

      const count = qrCodes?.length || 0;

      await supabase
        .from('profiles')
        .update({ total_qr_codes: count })
        .eq('id', userId);
    } catch (error) {
      console.warn('Failed to update user QR count:', error);
    }
  }

  // Record QR scan
  async recordQRScan(qrCodeId: string, metadata?: any): Promise<void> {
    try {
      // Get QR code info
      const { data: qrCode } = await supabase
        .from('qr_codes')
        .select('campaign_id')
        .eq('id', qrCodeId)
        .single();

      if (!qrCode) return;

      // Insert scan record into qr_scans table (single source of truth for scans)
      await supabase
        .from('qr_scans')
        .insert({
          qr_code_id: qrCodeId,
          campaign_id: qrCode.campaign_id,
          user_agent: metadata?.userAgent,
          ip_address: metadata?.ipAddress,
          location: metadata?.location,
          device_type: metadata?.deviceType,
          referrer: metadata?.referrer,
          scan_timestamp: new Date().toISOString()
        });

      // Note: total_scans column has been removed - scans are calculated dynamically from qr_scans table
    } catch (error) {
      console.error('Error recording QR scan:', error);
    }
  }
}

export const campaignService = new CampaignService();
